import styled, { css } from "styled-components";
import Input from "common/Inputs/Input";
import Text from "common/Typography/Text";
import Title from "common/Typography/Title";
import { MOBILE_SEARCH } from "common/constants";
import { typography } from "@shared/common/Themes/typography";
import { hexToRGB } from "@shared/utils/misc";
import AntArrowRightOutlined from "@ant-design/icons/ArrowRightOutlined";
import ExpandingCard from "common/Boxes/ExpandingCard";
import Radio from "common/Radio/Radio";
import Checkbox from "common/Checkbox/Checkbox";
import Card from "common/Boxes/Card";
import CustomInputNumber from "common/Inputs/CustomInputNumber";

const StepWrapper = styled.div<{ $isMobile?: boolean }>`
  max-width: ${(p) => (p.$isMobile ? 400 : 950)}px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const StyledTitle = styled(Title)`
  text-align: center;
  margin-bottom: 24px;
  font-size: ${(p) => p.theme.typography.fontSizes.XXL};
  font-weight: ${(p) => p.theme.typography.fontWeights.BOLD};
  @media (max-width: ${MOBILE_SEARCH}) {
    font-size: 22px;
  }
`;

const Description = styled(Text)`
  margin-bottom: 20px;
  text-align: center;
  line-height: 24px;
`;

const inputStyles = `
  border-radius: 8px;
`;

const StyledInputNumber = styled(CustomInputNumber).attrs({
  stringMode: true,
  hideArrows: true,
  size: "large",
})`
  ${inputStyles};
`;

const CodeInputNumber = styled(CustomInputNumber).attrs({
  width: 44,
})`
  ${inputStyles};
  text-align: center;
  height: 46px;
  &:not(:last-child) {
    margin-right: 6px;
  }
`;

const InputDescription = styled(Text).attrs({
  $size: typography.fontSizes.S,
})`
  margin-top: 4px;
  margin-bottom: 32px;
`;

const StyledInputPassword = styled(Input.Password).attrs({
  size: "large",
})`
  ${inputStyles}
`;

const StyledInput = styled(Input).attrs({
  size: "large",
})`
  ${inputStyles}
`;

const InfoBackground = styled.div`
  background: ${(p) => hexToRGB(p.theme.colors.primaryBlue, 0.05)};
  border-radius: 8px;
  padding: 24px;
`;

const BoldTitle = styled(Title).attrs({ level: 4 })`
  color: ${(p) => p.theme.colors.primaryGrey};
  font-weight: ${(p) => p.theme.typography.fontWeights.BOLD};
  margin-bottom: 8px;
`;

const ArrowRight = styled(AntArrowRightOutlined)`
  font-size: ${(p) => p.theme.typography.fontSizes.L};
  color: ${(p) => p.theme.colors.primaryGrey};
`;

const StyledExpandingCard = styled(ExpandingCard)`
  background: ${(p) => hexToRGB(p.theme.colors.primaryBlue, 0.05)};
  box-shadow: none;
  margin-top: 8px;
`;

const ExpandingCardChildrenText = styled(Text)`
  margin-right: 16px;
`;

const Logo = styled.img<{ $small: boolean }>`
  height: ${(p) => (p.$small ? 40 : 55)}px;
  width: ${(p) => (p.$small ? 40 : 55)}px;
  border-radius: 8px;
  margin-right: ${(p) => (p.$small ? 12 : 20)}px;
`;

const ModalTitle = styled(Title).attrs({ level: 2 })`
  font-weight: ${(p) => p.theme.typography.fontWeights.BOLD};
  text-align: center;
`;

const StyledRadio = styled(Radio)`
  width: 100%;
  span.ant-radio + * {
    width: 100%;
    padding-right: 0;
  }
  &.ant-radio-wrapper {
    margin: 0;
    .ant-radio {
      align-self: baseline;
    }
  }
  .ant-radio-inner {
    margin-bottom: 2px;
  }
`;

const StyledCheckbox = styled(Checkbox)`
  .ant-checkbox + span {
    width: 100%;
    padding-right: 0;
  }
`;

const OverviewCard = styled(Card)<{ $isMobile: boolean }>`
  padding: ${(p) => (p.$isMobile ? 16 : 24)}px;
`;

const messageStyle = css<{ $isMobile: boolean }>`
  margin-top: 4px;
  font-size: ${(p) =>
    p.$isMobile
      ? p.theme.typography.fontSizes.S
      : p.theme.typography.fontSizes.M};
`;

const ErrorText = styled(Text)<{ $isMobile: boolean }>`
  ${messageStyle};
  color: ${(p) => p.theme.colors.red};
`;

const WarningText = styled(Text)<{ $isMobile: boolean }>`
  ${messageStyle};
  color: ${(p) => p.theme.colors.gold};
`;

const OverviewTitle = styled(Title).attrs({ level: 4 })`
  color: ${(p) => p.theme.colors.primaryGrey};
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
  margin-bottom: 8px;
`;

const ConfirmMessage = styled(Text).attrs({ type: "secondary" })`
  font-size: ${(p) => p.theme.typography.fontSizes.S};
  text-align: start;
`;

export const OnboardingStyles = {
  StepWrapper,
  Title: StyledTitle,
  Description,
  Input: StyledInput,
  InputNumber: StyledInputNumber,
  InputPassword: StyledInputPassword,
  InputDescription,
  CodeInputNumber,
  InfoBackground,
  BoldTitle,
  ArrowRight,
  ExpandingCard: StyledExpandingCard,
  ExpandingCardChildrenText,
  Logo,
  ModalTitle,
  Radio: StyledRadio,
  Checkbox: StyledCheckbox,
  OverviewCard,
  ErrorText,
  WarningText,
  OverviewTitle,
  ConfirmMessage,
};
